import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import PasswordInput from "../components/PasswordInput";
import useSEO from "../hooks/useSEO";

export default function ResetPassword() {
  useSEO({ title: "Réinitialiser le mot de passe", description: "Définissez un nouveau mot de passe pour votre compte DemkaBiz." });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères."); return; }
    if (pwd !== confirm) { toast.error("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Mot de passe réinitialisé avec succès.");
      setTimeout(() => navigate("/connexion", { replace: true }), 2500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lien invalide ou expiré.");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="reset-password-page">
        <h1 className="font-display font-bold text-2xl">Lien invalide</h1>
        <p className="text-muted-foreground mt-2">Aucun jeton de réinitialisation détecté.</p>
        <Link to="/mot-de-passe-oublie" className="btn-primary mt-6 inline-flex">Demander un nouveau lien</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="reset-password-page">
        <CheckCircle2 className="w-16 h-16 mx-auto text-primary" />
        <h1 className="font-display font-bold text-3xl mt-4">Mot de passe réinitialisé !</h1>
        <p className="text-muted-foreground mt-2">Vous allez être redirigé vers la connexion…</p>
        <Link to="/connexion" className="btn-primary mt-6 inline-flex" data-testid="reset-success-login-btn">Se connecter</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="reset-password-page">
      <h1 className="font-display font-bold text-3xl tracking-tight">Nouveau mot de passe</h1>
      <p className="text-muted-foreground mt-2">Choisissez un mot de passe sécurisé pour votre compte.</p>
      <form onSubmit={submit} className="space-y-4 mt-8" data-testid="reset-form">
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Nouveau mot de passe</label>
          <div className="mt-1">
            <PasswordInput
              dataTestId="reset-password-input"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required minLength={6}
              autoComplete="new-password"
              className="pl-4"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Confirmer le mot de passe</label>
          <div className="mt-1">
            <PasswordInput
              dataTestId="reset-password-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required minLength={6}
              autoComplete="new-password"
              className="pl-4"
            />
          </div>
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="reset-submit">
          {loading ? "Mise à jour…" : "Réinitialiser le mot de passe"}
        </button>
      </form>
      <Link to="/connexion" className="text-sm text-primary hover:underline mt-6 inline-block">← Retour à la connexion</Link>
    </div>
  );
}
